import './App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useEffect, lazy, Suspense } from 'react';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Critical: loaded eagerly (homepage is the landing page)
import HomePage from './pages/HomePage';

// Everything else: lazy-loaded (code-split into separate chunks)
const MeetOurTeam = lazy(() => import('./pages/MeetOurTeam'));
const WeAreIndependent = lazy(() => import('./pages/WeAreIndependent'));
const InsuranceCompanies = lazy(() => import('./pages/InsuranceCompanies'));
const LearningCenter = lazy(() => import('./pages/LearningCenter'));
const GoogleReviews = lazy(() => import('./pages/GoogleReviews'));
const ClickableCoverage = lazy(() => import('./pages/ClickableCoverage'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const BlogEditor = lazy(() => import('./pages/BlogEditor'));
const AdminMovedNotice = lazy(() => import('./pages/AdminMovedNotice'));
const ResourceCenter = lazy(() => import('./pages/resources/ResourceCenter'));
const Calculator = lazy(() => import('./pages/resources/Calculator'));
const Checklist = lazy(() => import('./pages/resources/Checklist'));
const Explainers = lazy(() => import('./pages/resources/Explainers'));
const MarketReports = lazy(() => import('./pages/resources/MarketReports'));
const VAEscrowCalculator = lazy(() => import('./pages/resources/VAEscrowCalculator'));
const RiskScore = lazy(() => import('./pages/resources/RiskScore'));
const ChecklistsDownload = lazy(() => import('./pages/resources/ChecklistsDownload'));
const NorthAugustaSC = lazy(() => import('./pages/locations/NorthAugustaSC'));
const AikenSC = lazy(() => import('./pages/locations/AikenSC'));
const AugustaGA = lazy(() => import('./pages/locations/AugustaGA'));
const EvansGA = lazy(() => import('./pages/locations/EvansGA'));
const MartinezGA = lazy(() => import('./pages/locations/MartinezGA'));
const GrovetownGA = lazy(() => import('./pages/locations/GrovetownGA'));
const ColumbiaSC = lazy(() => import('./pages/locations/ColumbiaSC'));
const GreenvilleSC = lazy(() => import('./pages/locations/GreenvilleSC'));
const CharlestonSC = lazy(() => import('./pages/locations/CharlestonSC'));
const AtlantaGA = lazy(() => import('./pages/locations/AtlantaGA'));
const HomeInsurance = lazy(() => import('./pages/services/HomeInsurance'));
const AutoInsurance = lazy(() => import('./pages/services/AutoInsurance'));
const BusinessInsurance = lazy(() => import('./pages/services/BusinessInsurance'));
const GeneralLiability = lazy(() => import('./pages/services/GeneralLiability'));
const WorkersComp = lazy(() => import('./pages/services/WorkersComp'));
const LandlordInsurance = lazy(() => import('./pages/services/LandlordInsurance'));
const RVInsurance = lazy(() => import('./pages/services/RVInsurance'));
const VALoanInsurance = lazy(() => import('./pages/loan-guides/VALoanInsurance'));
const FHALoanInsurance = lazy(() => import('./pages/loan-guides/FHALoanInsurance'));
const ConventionalLoan = lazy(() => import('./pages/loan-guides/ConventionalLoan'));
const USDALoan = lazy(() => import('./pages/loan-guides/USDALoan'));
const FindCoverageQuiz = lazy(() => import('./pages/FindCoverageQuiz'));
const QuoteExperience = lazy(() => import('./pages/quote/QuoteExperience'));
const FAQ = lazy(() => import('./pages/FAQ'));
const NotFound = lazy(() => import('./pages/NotFound'));
const SavingsCalculator = lazy(() => import('./pages/resources/SavingsCalculator'));
const CommunityDirectory = lazy(() => import('./pages/resources/CommunityDirectory'));
const ReferralLogin = lazy(() => import('./pages/referral/ReferralLogin'));
const ReferralSignup = lazy(() => import('./pages/referral/ReferralSignup'));
const ReferralDashboard = lazy(() => import('./pages/referral/ReferralDashboard'));
const FounderLetterPage = lazy(() => import('./pages/FounderLetterPage'));
const MobileAppPage = lazy(() => import('./pages/MobileAppPage'));
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('./pages/legal/TermsAndConditions'));
const CookiePolicy = lazy(() => import('./pages/legal/CookiePolicy'));
const Disclaimer = lazy(() => import('./pages/legal/Disclaimer'));
const PartnerHub = lazy(() => import('./pages/PartnerHub'));
const QuickQuote = lazy(() => import('./pages/QuickQuote'));
const ProducerOpportunity = lazy(() => import('./pages/ProducerOpportunity'));
const CsrOpportunity = lazy(() => import('./pages/CsrOpportunity'));
const CRMLayout = lazy(() => import('./pages/crm/CRMLayout'));
const HomeInsuranceLanding = lazy(() => import('./pages/landing/HomeInsuranceLanding'));

import FloatingCallButton from './components/engagement/FloatingCallButton';
import ExitIntentPopup from './components/engagement/ExitIntentPopup';
import SocialProof from './components/engagement/SocialProof';
import NotificationBar from './components/NotificationBar';
import { Toaster } from 'sonner';

// Minimal loading fallback — keeps layout stable during chunk load
const PageLoader = () => (
  <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ width: 32, height: 32, border: '3px solid #e5e7eb', borderTop: '3px solid #f92626', borderRadius: '50%', animation: 'spin .6s linear infinite' }} />
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

function HideOnCRM({ children }) {
  const { pathname } = useLocation();
  if (pathname.startsWith('/crm') || pathname.startsWith('/home-insurance-quote') || pathname.startsWith('/get-a-quote')) return null;
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <HideOnCRM>
          <NotificationBar />
          <FloatingCallButton />
          <ExitIntentPopup />
          <SocialProof />
        </HideOnCRM>
        <Suspense fallback={<PageLoader />}>
        <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/meet-our-team" element={<MeetOurTeam />} />
            <Route path="/we-are-independent" element={<WeAreIndependent />} />
            <Route path="/founder-letter" element={<FounderLetterPage />} />
            <Route path="/mobile-app" element={<MobileAppPage />} />
            <Route path="/insurance-companies" element={<InsuranceCompanies />} />
            <Route path="/learning-center" element={<LearningCenter />} />
            <Route path="/review-our-agency" element={<GoogleReviews />} />
            <Route path="/clickable-coverage" element={<ClickableCoverage />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/admin" element={<AdminMovedNotice />} />
            <Route path="/admin/blog" element={<AdminMovedNotice />} />
            <Route path="/admin/write" element={<BlogEditor />} />
            <Route path="/resources" element={<ResourceCenter />} />
            <Route path="/resources/calculator" element={<Calculator />} />
            <Route path="/resources/checklist" element={<Checklist />} />
            <Route path="/resources/explainers" element={<Explainers />} />
            <Route path="/resources/market-reports" element={<MarketReports />} />
            <Route path="/resources/va-escrow-calculator" element={<VAEscrowCalculator />} />
            <Route path="/resources/risk-score" element={<RiskScore />} />
            <Route path="/resources/checklists-download" element={<ChecklistsDownload />} />
            
            {/* Location Pages */}
            <Route path="/locations/north-augusta-sc" element={<NorthAugustaSC />} />
            <Route path="/locations/aiken-sc" element={<AikenSC />} />
            <Route path="/locations/augusta-ga" element={<AugustaGA />} />
            <Route path="/locations/evans-ga" element={<EvansGA />} />
            <Route path="/locations/martinez-ga" element={<MartinezGA />} />
            <Route path="/locations/grovetown-ga" element={<GrovetownGA />} />
            <Route path="/locations/columbia-sc" element={<ColumbiaSC />} />
            <Route path="/locations/greenville-sc" element={<GreenvilleSC />} />
            <Route path="/locations/charleston-sc" element={<CharlestonSC />} />
            <Route path="/locations/atlanta-ga" element={<AtlantaGA />} />
            
            {/* Service Pages */}
            <Route path="/services/home-insurance" element={<HomeInsurance />} />
            <Route path="/services/auto-insurance" element={<AutoInsurance />} />
            <Route path="/services/business-insurance" element={<BusinessInsurance />} />
            <Route path="/services/general-liability" element={<GeneralLiability />} />
            <Route path="/services/workers-comp" element={<WorkersComp />} />
            <Route path="/services/landlord-insurance" element={<LandlordInsurance />} />
            <Route path="/services/rv-insurance" element={<RVInsurance />} />
            
            {/* Loan Guide Pages */}
            <Route path="/loan-guides/va-loan" element={<VALoanInsurance />} />
            <Route path="/loan-guides/fha-loan" element={<FHALoanInsurance />} />
            <Route path="/loan-guides/conventional-loan" element={<ConventionalLoan />} />
            <Route path="/loan-guides/usda-loan" element={<USDALoan />} />
            
            {/* Coverage Quiz */}
            <Route path="/find-my-coverage" element={<FindCoverageQuiz />} />
            <Route path="/get-a-quote" element={<QuoteExperience />} />
            {/* Legacy quote URLs (old bookmarks / ads / indexed pages) → the AI quote experience */}
            {['/quote', '/quotes', '/get-quote', '/free-quote', '/request-a-quote', '/request-quote', '/quote-request', '/insurance-quote', '/get-a-quote/*'].map(p => (
              <Route key={p} path={p} element={<Navigate to="/get-a-quote" replace />} />
            ))}
            <Route path="/faq" element={<FAQ />} />
            <Route path="/resources/savings-calculator" element={<SavingsCalculator />} />
            <Route path="/resources/community-directory" element={<CommunityDirectory />} />

            {/* Referral Partner Portal */}
            <Route path="/referral/login" element={<ReferralLogin />} />
          <Route path="/referral/signup" element={<ReferralSignup />} />
            <Route path="/referral/dashboard" element={<ReferralDashboard />} />

            {/* Hidden partner page — no nav link, direct URL access only */}
            <Route path="/partner-hub" element={<PartnerHub />} />
            <Route path="/quick-quote" element={<QuickQuote />} />
            <Route path="/home-insurance-quote" element={<HomeInsuranceLanding />} />
            <Route path="/home-insurance-quote/:citySlug" element={<HomeInsuranceLanding />} />
            <Route path="/producer-opportunity" element={<ProducerOpportunity />} />
            <Route path="/csr-opportunity" element={<CsrOpportunity />} />

            {/* Internal Pipeline CRM — hidden, accessed via footer link */}
            <Route path="/crm/*" element={<CRMLayout />} />

            {/* Legal Pages */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/disclaimer" element={<Disclaimer />} />

            {/* 404 Catch-all */}
            <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-center" richColors closeButton />
    </div>
  );
}

export default App;
